/**
 * Location Model Helper Functions
 * 
 * Supports both v1 (mainCity, cities) and v2 (country, city, department) models
 * with full backward compatibility.
 */

/**
 * Get display location string from hairstylist data
 * Supports both v1 and v2 location models
 * 
 * @param {Object} hairstylist - Hairstylist object with location data
 * @returns {string} Display location string
 */
export function getDisplayLocation(hairstylist) {
  // V2 Model: Use country, city, department if available
  if (hairstylist.city) {
    const country = hairstylist.country;
    const city = hairstylist.city;
    const department = hairstylist.department;

    // If France and department exists, show "City (Department)"
    if (country === 'FR' && department) {
      return `${city} (${department})`;
    }
    
    // Otherwise, just return the city
    return city;
  }

  // V1 Model: Fallback to mainCity or cities[0]
  if (hairstylist.mainCity) {
    return hairstylist.mainCity;
  }

  if (hairstylist.cities && Array.isArray(hairstylist.cities) && hairstylist.cities.length > 0) {
    return hairstylist.cities[0];
  }

  // No location data available
  return 'Localisation non renseignée';
}

/**
 * Get the primary city for search/filtering
 * Returns v2.city if available, otherwise falls back to v1
 * 
 * @param {Object} hairstylist - Hairstylist object with location data
 * @returns {string|null} Primary city string or null
 */
export function getPrimaryCity(hairstylist) {
  // V2 Model: Use city if available
  if (hairstylist.city) {
    return hairstylist.city;
  }

  // V1 Model: Fallback to mainCity or cities[0]
  if (hairstylist.mainCity) {
    return hairstylist.mainCity;
  }

  if (hairstylist.cities && Array.isArray(hairstylist.cities) && hairstylist.cities.length > 0) {
    return hairstylist.cities[0];
  }

  return null;
}

/**
 * Get the country for search/filtering
 * Returns v2.country if available, otherwise defaults to "BE" for V1 profiles
 * (since only Belgian hairstylists can be in V1 model - all FR and CH are in V2)
 * Always returns uppercase country code for consistency (case insensitive)
 * 
 * @param {Object} hairstylist - Hairstylist object with location data
 * @returns {string|null} Country code (FR, BE, CH) in uppercase or null
 */
export function getCountry(hairstylist) {
  // V2 Model: Use country if available
  if (hairstylist.country) {
    return hairstylist.country.toUpperCase();
  }

  // V1 Model: Default to "BE" since only Belgian hairstylists can be in V1
  // All FR and CH hairstylists are in V2 and have the country field
  if (hairstylist.mainCity || (hairstylist.cities && hairstylist.cities.length > 0)) {
    return 'BE';
  }

  return null;
}

/**
 * Check if hairstylist has location data (v1 or v2)
 * 
 * @param {Object} hairstylist - Hairstylist object with location data
 * @returns {boolean} True if location data exists
 */
export function hasLocation(hairstylist) {
  // V2 Model
  if (hairstylist.city) {
    return true;
  }

  // V1 Model
  if (hairstylist.mainCity) {
    return true;
  }

  if (hairstylist.cities && Array.isArray(hairstylist.cities) && hairstylist.cities.length > 0) {
    return true;
  }

  return false;
}

